import {
  Box,
  CircularProgress,
  Collapse,
  Divider,
  Typography,
} from '@mui/material';
import { ReactElement, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AuthenticationService } from 'services/AuthenticationService';
import { Utils } from 'shared/helpers';
import { AuthActions } from 'state/authentication/Action';
import { BaseAppActions } from 'state/baseapplication/Action';
import {
  BaseApplicationState,
  Organization,
} from 'state/baseapplication/Reducer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface IOrganisationListProps {
  onOrgChange: () => void;
}
export const OrganisationList: React.FC<IOrganisationListProps> = (props) => {
  const { onOrgChange } = props;
  const { orgs, activeOrg }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  const [openMenu, setOpenMenu] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const reduxDispatch = useDispatch();
  const navigate = useNavigate();

  // API Calls
  const loadAccessToken = (org: Organization): void => {
    setShowLoader(true);
    AuthenticationService.getOrgAccessToken({
      orgId: org.id,
    })
      .then((response) => {
        const { data } = response.data;
        reduxDispatch(AuthActions.setLoginStatus(data));
        reduxDispatch(BaseAppActions.setActiveOrg(org));
        reduxDispatch(BaseAppActions.setOrgAclConfig(data.configs));
        navigate('/overview', { replace: true });
        setShowLoader(false);
        setOpenMenu(!openMenu);
        onOrgChange();
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        setShowLoader(false);
      });
  };

  // Instance Methods
  const orgListToShow = useMemo(
    () => orgs.filter((org) => activeOrg.id !== org.id),
    [orgs, activeOrg]
  );

  const getOrgDetail = (org: Organization): ReactElement => (
    <Box>
      <Typography className="fw-6 f18">{org.name}</Typography>
      <Typography className="fw-4 f14">{org.role || 'Admin'}</Typography>
    </Box>
  );

  // UI Actions
  const actionSetOrganizationList = (org: Organization): void => {
    loadAccessToken(org);
  };

  // UI Elements
  return (
    <Box className="org-selection-wrapper">
      <Box
        className="org-active"
        onClick={() => {
          if (!showLoader && orgListToShow.length > 0) {
            setOpenMenu(!openMenu);
          }
        }}
      >
        {getOrgDetail(activeOrg)}
        {orgListToShow.length > 0 && (
          <Box>
            {!openMenu && <ExpandMoreIcon className="icon-drop-down-style" />}
            {openMenu && <ExpandLessIcon className="icon-drop-down-style" />}
          </Box>
        )}
      </Box>
      <Collapse in={openMenu} className="org-list-dropdown">
        {showLoader && (
          <Box
            className="loader-section"
            display="flex"
            justifyContent="center"
            p={5}
          >
            <CircularProgress />
          </Box>
        )}

        {!showLoader &&
          orgListToShow.map((org: Organization, index) => (
            <>
              <Box
                className="org-info"
                key={org.id}
                onClick={() => actionSetOrganizationList(org)}
              >
                {getOrgDetail(org)}
              </Box>
              {index < orgListToShow.length - 1 && <Divider className="my-2" />}
            </>
          ))}
      </Collapse>
    </Box>
  );
};

export default OrganisationList;
